import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { useSceneStore } from "@/engine/SceneManager";
import type { SceneId } from "@/scenes/SceneContract";
import { AudioManager } from "@/engine/AudioManager";

/**
 * 씬 좌상단 "← 뒤로" 버튼. 기본 동작은 SceneManager pop().
 * `replaceTo` 가 주어지면 그 씬으로 교체 (pop 이 아니라 replace) — 예를 들어 RunForge 같은
 * 서브플로우의 첫 단계에서 home 으로 돌아가고 싶을 때.
 */
interface BackButtonProps {
    /** 지정 시 replace(sceneId) — 스택을 태우지 않고 지정 씬으로 교체. */
    replaceTo?: SceneId;
    /** 커스텀 라벨. 기본 "뒤로". */
    label?: string;
    /** 커스텀 onClick — 제공 시 pop/replace 대신 실행. */
    onClick?: () => void;
    /** 상단에서의 거리 (tailwind 토큰). 기본 l(24). HUD 있는 씬은 xl 로 올림. */
    top?: "s" | "m" | "l" | "xl";
}

export function BackButton({ replaceTo, label = "뒤로", onClick, top = "l" }: BackButtonProps) {
    const pop = useSceneStore(s => s.pop);
    const replace = useSceneStore(s => s.replace);

    const handle = () => {
        AudioManager.sfx(tokens.audio.sfx.buttonTap);
        if (onClick) { onClick(); return; }
        if (replaceTo) { void replace(replaceTo); return; }
        void pop();
    };

    const topPx = top === "s" ? 8 : top === "m" ? 16 : top === "l" ? 24 : 40;

    // 2026-04-23 사용자 피드백: "홈으로 버튼이 꼭 항상 좌측 상단에 떠있을만큼 자주 누르는
    //   버튼은 아니다". 평소에는 ← 화살표 아이콘 하나만 흐리게, 호버 시에만 라벨이 슬쩍
    //   펼쳐지는 형태로 바꿔 시각적 점유를 대폭 낮춘다. Esc 단축키도 그대로 유효.
    return (
        <motion.button
            type="button"
            onClick={handle}
            initial={{ opacity: 0.92 }}
            whileHover={{ opacity: 1, scale: 1.04, x: -2 }}
            whileTap={{ scale: 0.96 }}
            className="group font-display rounded-pill flex items-center gap-xs overflow-hidden"
            style={{
                position: "absolute",
                left: 24,
                top: topPx,
                zIndex: tokens.elevation.hud + 6,
                padding: "7px 12px",
                background: `${tokens.color.ink}AA`,
                border: `1px solid ${tokens.color.brass}66`,
                color: tokens.color.parchment,
                fontSize: tokens.type.size.small,
                letterSpacing: tokens.type.letterSpacing.wide,
                backdropFilter: "blur(4px)",
                boxShadow: `0 0 20px ${tokens.color.brass}24, inset 0 1px 0 ${tokens.color.parchment}22`,
                textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 14px`,
            }}
            aria-label={label}
            title={label}
        >
            <span style={{ fontSize: tokens.type.size.small, lineHeight: 1 }}>←</span>
            {/* 라벨 — 평소엔 width 0 으로 감춰 두고 hover 시 펼침. */}
            <span
                className="whitespace-nowrap overflow-hidden transition-all duration-200 group-hover:max-w-[200px] group-hover:ml-[4px] max-w-0 ml-0"
            >
                {label}
            </span>
        </motion.button>
    );
}
